import { delay, oneSecondInMs } from '@repo/utils'
import { getBlockNumber } from 'viem/actions'

import type { WaitContext } from '../types.action-flow'

import { getChain } from '../../../utils/chain.utils'
import { assertPositiveInteger } from './assertPositiveInteger'

/**
 * Fallback block time for chains where viem's chain definition does not carry
 * `blockTime` (e.g. some L2s viem hasn't catalogued yet). The
 * `waitForConfirmations(n)` builder self-bounds at a small multiple of
 * `n × blockTime`, so a wedged RPC or a chain halt surfaces as a callback
 * timeout instead of letting the engine hang on the next call's gas
 * estimation. 12s is the mainnet block time and a safe ceiling for unknown
 * chains.
 */
const DEFAULT_BLOCK_TIME_MS = 12 * oneSecondInMs

/**
 * Cap multiplier over expected `n × blockTime` before the builder gives up.
 * A small multiple absorbs ordinary clock skew and the occasional skipped
 * block; anything beyond it indicates a real propagation problem worth
 * surfacing. 2 keeps the deadline tight enough that a wedged RPC trips
 * inside a few block intervals instead of letting users stare at "Waiting…"
 * for minutes.
 */
const TIMEOUT_MULTIPLIER = 2

const POLL_INTERVAL_MS = oneSecondInMs

/**
 * Canonical `after` (or `before`) callback builder for deploy →
 * use-deployed-contract flows. Polls the engine's `publicClient` until the
 * receipt's block has at least `n` confirmations, then resolves — giving the
 * wallet's own RPC node time to catch up before the next call simulates
 * against the just-deployed contract.
 *
 * Null-tolerant on `after`: when `priorReceipt` is null (publicClient failed
 * to fetch the receipt the wallet already confirmed), the builder
 * short-circuits and the engine re-engages its blanket inter-tx delay. This
 * never fires for `before` on the first call because that gate receives no
 * prior receipt; every later `before` site has one.
 *
 * Self-bounded: throws if the chain's block tip hasn't caught up after a
 * short multiple of `n × blockTime`. The engine routes throws through its
 * `execution-error` path.
 */
export function waitForConfirmations(n: number): (ctx: WaitContext) => Promise<void> {
  assertPositiveInteger(n, 'waitForConfirmations')
  return async (ctx: WaitContext) => {
    if (!ctx.receipt) return
    const targetBlock = ctx.receipt.blockNumber + BigInt(n)
    // viem's Chain.blockTime is in milliseconds and covers most supported chains
    // we ship. `getChain` throws for ids outside `ChainId`, so the default only
    // covers supported chains viem has not yet catalogued.
    const blockTimeMs = getChain(ctx.chainId).blockTime ?? DEFAULT_BLOCK_TIME_MS
    const deadlineMs = Date.now() + n * blockTimeMs * TIMEOUT_MULTIPLIER

    while (!ctx.signal.aborted) {
      // Call as a standalone viem action: the engine's `publicClient` is a
      // wagmi `Client` (from `useClient`), which doesn't carry the bound
      // action methods that `createPublicClient` adds.
      //
      // `cacheTime: 0` bypasses viem's per-client block-number cache
      // (default ~4s on HTTP transports). Fast chains (Arbitrum 250ms,
      // Monad 400ms, etc.) have deadlines shorter than that cache window,
      // so stale tips would otherwise cause spurious timeouts.
      const tip = await getBlockNumber(ctx.publicClient, { cacheTime: 0 })
      if (tip >= targetBlock) return
      if (Date.now() > deadlineMs) {
        const timeoutSeconds = Math.ceil((n * blockTimeMs * TIMEOUT_MULTIPLIER) / oneSecondInMs)
        throw new Error(
          `[waitForConfirmations] chain ${ctx.chainId} did not reach block ${targetBlock} ` +
            `within ${timeoutSeconds}s of receipt block ${ctx.receipt.blockNumber}; ` +
            `last tip ${tip}.`
        )
      }
      await delay(POLL_INTERVAL_MS)
    }
  }
}
